// Emitted in place of a Google result when a restricted capability is requested
// but the org's Workspace Service Account integration isn't usable. This is a
// card, not a failure: the tool succeeded in detecting the gap.
export interface GoogleSetupRequiredPayload {
  type: "google_setup_required";
  reason: "workspace_not_configured" | "not_exposed" | "domain_mismatch" | "scope_not_authorized";
  capability?: string;
  missingScopes?: string[];
}

const REASONS: ReadonlySet<GoogleSetupRequiredPayload["reason"]> = new Set([
  "workspace_not_configured",
  "not_exposed",
  "domain_mismatch",
  "scope_not_authorized",
]);

const isReason = (v: unknown): v is GoogleSetupRequiredPayload["reason"] =>
  typeof v === "string" && REASONS.has(v as GoogleSetupRequiredPayload["reason"]);

// An unrecognized `reason` defaults to `workspace_not_configured`.
export const parseGoogleSetupRequiredPayload = (v: unknown): GoogleSetupRequiredPayload | null => {
  if (!v || typeof v !== "object") return null;
  const r = v as Record<string, unknown>;
  if (r.type !== "google_setup_required") return null;
  return {
    type: "google_setup_required",
    reason: isReason(r.reason) ? r.reason : "workspace_not_configured",
    ...(typeof r.capability === "string" ? { capability: r.capability } : {}),
    ...(Array.isArray(r.missingScopes) && r.missingScopes.every((s): s is string => typeof s === "string")
      ? { missingScopes: r.missingScopes }
      : {}),
  };
};
